import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, Row, TextField } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import VideoPlayer from '~/components/VideoPlayer/VideoPlayer';
import { VideoUrlInput } from '~/components/VideoUrlInput/VideoUrlInput';
import { useStore } from '~/contexts';
import { useCreateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useCreateCustomPageTemplate';
import { useTemplateSaveErrorHandler } from '~/hooks/reactQuery/mutations/useTemplateSaveErrorHandler';
import { useUpdateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useUpdateCustomPageTemplate';
import { useCustomPageTemplates } from '~/hooks/reactQuery/queries/useCustomPageTemplates';
import { useLocaleKeys } from '~/i18n';
import { pageTitleValidation } from '~/models/custom-page-template/pageTitleValidation';
import { templateNameValidation } from '~/models/custom-page-template/templateNameValidation';
import { selectors } from '~/store/state-selectors';
import { connectControllerToTextField } from '~/utils/react-hook-form.utils';
import { isUrlValid } from '~/utils/videoUrl/videoUrl';
import s from './VideoTemplateFormModal.module.scss';
export const GetVideoTemplateFormModalTID = (parentTestId)=>getTestIds(parentTestId);
export const VideoTemplateForm = (param)=>{
    let { mode, template, onBack, onSaved } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetVideoTemplateFormModalTID(), []);
    const businessId = useStore(selectors.user.businessId);
    const { templates: existingTemplates } = useCustomPageTemplates();
    const { control, handleSubmit, setError, watch, formState } = useForm({
        mode: 'onChange',
        defaultValues: {
            name: mode === 'edit' && template ? template.name : '',
            pageTitle: mode === 'edit' && template ? template.pageTitle : '',
            videoUrl: mode === 'edit' && template ? template.video.videoUrl : ''
        }
    });
    const { createTemplate, isLoading: isCreateLoading } = useCreateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const { updateTemplate, isLoading: isUpdateLoading } = useUpdateCustomPageTemplate({
        onSuccess: ()=>onSaved()
    });
    const handleSaveError = useTemplateSaveErrorHandler({
        mode,
        setNameError: (message)=>setError('name', {
                message
            })
    });
    const isLoading = isCreateLoading || isUpdateLoading;
    const onSubmit = handleSubmit((values)=>{
        if (isLoading) return;
        const payload = {
            type: 'video',
            name: values.name.trim(),
            pageTitle: values.pageTitle.trim(),
            displayScope: 'proposalOnly',
            video: {
                videoUrl: values.videoUrl
            }
        };
        if (mode === 'create') {
            createTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                data: payload
            }, {
                onError: handleSaveError
            });
        } else if (template) {
            updateTemplate({
                businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                templateId: template.id,
                data: payload
            }, {
                onError: handleSaveError
            });
        }
    });
    const videoUrl = watch('videoUrl');
    const showVideoPreview = isUrlValid(videoUrl);
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.VideoTemplateFormModalTestIds.Root(),
        className: s.formContent,
        children: [
            /*#__PURE__*/ _jsx(Controller, {
                name: "name",
                control: control,
                rules: templateNameValidation({
                    t: t.customPages.templates,
                    existingTemplates,
                    excludeTemplateId: template === null || template === void 0 ? void 0 : template.id
                }),
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps, {
                        alwaysShowErrors: true
                    })), {
                        testId: testIds.VideoTemplateFormModalTestIds.NameField(),
                        label: t.customPages.templates.nameLabel(),
                        bi: {
                            name: 'video_template_form_name'
                        }
                    }))
            }),
            /*#__PURE__*/ _jsx(Controller, {
                name: "pageTitle",
                control: control,
                rules: pageTitleValidation(t.customPages),
                render: (renderProps)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({}, connectControllerToTextField(renderProps, {
                        alwaysShowErrors: true
                    })), {
                        testId: testIds.VideoTemplateFormModalTestIds.PageTitleField(),
                        label: t.customPages.templates.pageTitleLabel(),
                        bi: {
                            name: 'video_template_form_page_title'
                        }
                    }))
            }),
            /*#__PURE__*/ _jsx(Controller, {
                name: "videoUrl",
                control: control,
                rules: {
                    validate: (value)=>isUrlValid(value) ? undefined : ''
                },
                render: (param)=>{
                    let { field } = param;
                    return /*#__PURE__*/ _jsx(VideoUrlInput, {
                        value: field.value,
                        onChange: field.onChange,
                        onBlur: field.onBlur,
                        required: true,
                        submitAttempted: formState.isSubmitted,
                        testId: testIds.VideoTemplateFormModalTestIds.VideoUrlInput()
                    });
                }
            }),
            showVideoPreview && /*#__PURE__*/ _jsx(Box, {
                testId: testIds.VideoTemplateFormModalTestIds.VideoPreview(),
                className: s.videoPreview,
                children: /*#__PURE__*/ _jsx(VideoPlayer, {
                    url: videoUrl
                })
            }),
            /*#__PURE__*/ _jsxs(Row, {
                className: s.formFooter,
                justifyContent: "center",
                gap: 12,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        onClick: onBack,
                        testId: testIds.VideoTemplateFormModalTestIds.CancelButton(),
                        bi: {
                            name: 'video_template_form_cancel'
                        },
                        children: t.customPages.templates.cancel()
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        skin: "primary",
                        loading: isLoading,
                        onClick: onSubmit,
                        testId: testIds.VideoTemplateFormModalTestIds.SaveButton(),
                        bi: {
                            name: 'video_template_form_save'
                        },
                        children: t.customPages.templates.save()
                    })
                ]
            })
        ]
    });
};
